import React from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import dayjs from 'dayjs';
import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import SearchSelectFormField from 'src/registry/components/forms/SearchSelectFormField';
import CalendarWithTime from 'src/registry/components/inputs/CalendarWithTime';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
    DialogTrigger,
} from 'src/registry/ui/Dialog';
import {
    Form,
    FormControl,
    FormControlStack,
    FormDescription,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from 'src/registry/ui/Form';
import { generalMessages } from 'src/registry/utils/messages';
import { queryClient } from 'src/lib/react-query';
import { toast } from 'src/hooks/useToast';
import { useOnError } from 'src/utils/errors';
import { ICON_MAP } from 'src/utils/constants';
import { useListServicePlans } from 'src/features/servicePlans/api/useListServicePlans';
import { servicePlansMessages } from 'src/features/servicePlans/utils/messages';
import { Subscription } from 'src/features/subscriptions/utils/types';
import {
    schedulePlanChangeFormSchema,
    SchedulePlanChangeFormSchemaValues,
} from 'src/features/subscriptions/utils/formSchema';
import { useScheduleSubscriptionPlanChange } from 'src/features/subscriptions/api/useScheduleSubscriptionPlanChange';
import { LIST_SUBSCRIPTIONS_RESOURCE } from 'src/features/subscriptions/api/useListSubscriptions';
import { SUBSCRIPTION_RESOURCE } from 'src/features/subscriptions/api/useGetSubscription';
import { subscriptionsMessages } from 'src/features/subscriptions/utils/messages';

type SchedulePlanChangeFormModalProps = {
    subscription: Subscription;
    children?: React.ReactNode;
};

function SchedulePlanChangeFormModal({ subscription, children }: SchedulePlanChangeFormModalProps) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const [searchServicePlan, setSearchServicePlan] = React.useState<string>('');
    const { formatMessage } = useIntl();
    const onError = useOnError();

    const { data: servicePlans } = useListServicePlans({
        is_active: true,
        search: searchServicePlan,
    });

    /*
     * Filtering the current plan out client-side avoids the common `same_plan_change` 400. Currency
     * cannot be filtered - /subscriptions/service-plan/ exposes no currency parameter and its list
     * serializer does not return one - so `plan_change_currency_mismatch` is the backstop.
     */
    const selectablePlans = React.useMemo(
        () => (servicePlans?.results ?? []).filter((plan) => plan.id !== subscription.service_plan.id),
        [servicePlans, subscription.service_plan.id],
    );

    const form = useForm<SchedulePlanChangeFormSchemaValues>({
        resolver: zodResolver(schedulePlanChangeFormSchema),
        defaultValues: { service_plan: undefined, effective_date: undefined },
    });

    const { mutate, isPending } = useScheduleSubscriptionPlanChange({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [LIST_SUBSCRIPTIONS_RESOURCE] });
            void queryClient.invalidateQueries({ queryKey: [SUBSCRIPTION_RESOURCE] });
            setIsModalOpen(false);
            toast({
                title: formatMessage(subscriptionsMessages.schedulePlanChangeSuccess),
                description: formatMessage(subscriptionsMessages.schedulePlanChangeSuccessDescription, {
                    entity:
                        selectablePlans.find((plan) => plan.id === form.getValues('service_plan'))?.name ??
                        formatMessage(servicePlansMessages.servicePlan),
                }),
            });
        },
    });

    const onOpenChange = (isOpen: boolean) => {
        if (!isOpen) form.reset();
        setIsModalOpen(isOpen);
    };

    const onSubmit = React.useCallback(
        (values: SchedulePlanChangeFormSchemaValues) => {
            mutate(
                { subscriptionId: subscription.id, ...values },
                { onError: (error) => onError(error, form.setError) },
            );
        },
        [mutate, onError, form, subscription.id],
    );

    return (
        <Dialog modal open={isModalOpen} onOpenChange={onOpenChange}>
            <DialogTrigger asChild>{children}</DialogTrigger>
            <DialogContent>
                <DialogHeader>
                    <div className="flex flex-row gap-2">
                        <SquareIcon icon={ICON_MAP.SUBSCRIPTION} />
                        <div>
                            <DialogTitle>{formatMessage(subscriptionsMessages.schedulePlanChange)}</DialogTitle>
                            <DialogDescription>
                                {formatMessage(subscriptionsMessages.schedulePlanChangeSubtitle)}
                            </DialogDescription>
                        </div>
                    </div>
                </DialogHeader>
                <DialogSeparator />
                <DialogBody>
                    <Form form={form} onSubmit={form.handleSubmit(onSubmit)} variant="dialog">
                        <SearchSelectFormField
                            name="service_plan"
                            control={form.control}
                            label={formatMessage(subscriptionsMessages.targetServicePlan)}
                            items={selectablePlans}
                            clearable
                            placeholder={`${formatMessage(servicePlansMessages.servicePlan)}...`}
                            searchPlaceholder={formatMessage(servicePlansMessages.searchServicePlan)}
                            emptyTitle={formatMessage(servicePlansMessages.noServicePlan)}
                            emptyDescription={formatMessage(servicePlansMessages.noServicePlanFound)}
                            getItemValue={(plan) => plan.id}
                            getItemLabel={(plan) => plan.name}
                            getItemKeywords={(plan) => [plan.name]}
                            loadOptions={setSearchServicePlan}
                        />
                        <FormField
                            control={form.control}
                            name="effective_date"
                            render={({ field }) => (
                                <FormItem variant="dialog">
                                    <FormLabel variant="dialog">
                                        {formatMessage(subscriptionsMessages.effectiveDate)}
                                    </FormLabel>
                                    <FormControlStack>
                                        <FormControl>
                                            {/*
                                             * The API takes a plain YYYY-MM-DD. dayjs reads the picked instant back in local time, so the
                                             * day the user clicked is the day sent — toISOString() would shift it across midnight.
                                             */}
                                            <CalendarWithTime
                                                captionLayout="dropdown"
                                                dateISO={field.value ? dayjs(field.value).toISOString() : undefined}
                                                onChange={(dateISO) =>
                                                    field.onChange(dayjs(dateISO).format('YYYY-MM-DD'))
                                                }
                                                disableTime
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormControlStack>
                                </FormItem>
                            )}
                        />
                        <FormDescription>{formatMessage(subscriptionsMessages.effectiveDateHint)}</FormDescription>
                        <FormDescription>{formatMessage(subscriptionsMessages.planChangeItemsHint)}</FormDescription>
                    </Form>
                </DialogBody>
                <DialogSeparator />
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" type="button" className="flex-1">
                            <FormattedMessage {...generalMessages.cancel} />
                        </Button>
                    </DialogClose>
                    <Button
                        variant="default"
                        disabled={isPending}
                        type="submit"
                        onClick={form.handleSubmit(onSubmit)}
                        className="flex-1"
                    >
                        {isPending && <FontAwesomeIcon icon={ICON_MAP.SPINNER} className="mr-2 size-4 animate-spin" />}
                        {formatMessage(subscriptionsMessages.schedulePlanChange)}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default SchedulePlanChangeFormModal;
